import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import CircularProgress from '@/global/components/CircularProgress'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridCellParams } from '@/global/components/DataGrid/utils/types'
import Grid from '@/global/components/ui/Grid'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { FormField } from 'global/components/Form'
import { SelectFuncionarios } from 'global/components/Selects'
import { ContentGuide } from 'global/guides/ContentGuide'
import { useDebouncedValue } from 'hooks'
import { SendIcon } from 'lucide-react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { reps } from 'service/api'
import { toast } from 'sonner'
import { DesassociarFuncionarioRep } from '../modals/DesassociarFuncionario'

/** Janela de agrupamento dos cliques de reenvio, em milissegundos. */
const JANELA_REENVIO = 800

interface IEnvioContext {
    estaEnviando: (funcionarioId: number) => boolean
    solicitarEnvio: (funcionarioId: number) => void
}

const EnvioContext = createContext<IEnvioContext>({
    estaEnviando: () => false,
    solicitarEnvio: () => {},
})

/**
 * A célula precisa ser um componente estável, declarado fora do render: o
 * DataTable captura os colDefs no mount (`useColunasPersistidas`), então um
 * `renderCell` inline congelaria o estado do primeiro render e o botão nunca
 * trocaria para o spinner. O estado do envio chega por contexto.
 */
const EnviarFuncionarioCell: React.FC<GridCellParams> = ({ row }) => {
    const { estaEnviando, solicitarEnvio } = useContext(EnvioContext)
    const enviando = estaEnviando(row.funcionarioId)

    return (
        <IconButton
            title="Enviar ao equipamento"
            onClick={() => solicitarEnvio(row.funcionarioId)}
            disabled={enviando}
            className="relative"
        >
            <CircularProgress
                className={cn(
                    'absolute size-6 top-1/5 right-1/5 opacity-0',
                    'transition-opacity duration-500',
                    {
                        'opacity-100': enviando,
                    },
                )}
            />
            <SendIcon
                className={cn(
                    'size-6 opacity-100',
                    'transition-opacity duration-500',
                    {
                        'opacity-0': enviando,
                    },
                )}
            />
        </IconButton>
    )
}

export const AssociarFuncionarioForm = () => {
    const { watch } = useFormContext<{ id: number }>()
    const { id } = watch()
    const { data, isLoading } = reps.funcionarios.useFetchById(id)
    const { mutate } = reps.funcionarios.mutations.useCreate({
        invalidateQuery: true,
    })
    const { mutate: enviar, isLoading: isEnviando } =
        reps.enviar.mutations.useCreate()
    const [checkedIds, setCheckedIds] = useState<number[]>([])
    const [pendentes, setPendentes] = useState<number[]>([])
    /** Ids já despachados ao equipamento, aguardando resposta. */
    const [emVoo, setEmVoo] = useState<number[]>([])
    const pendentesDebounced = useDebouncedValue(pendentes, JANELA_REENVIO)

    const associados = (data as any)?.FuncionarioReps ?? []

    const checked = associados.map((item: any) => item.funcionarioId)

    const handleChange = (ids: number[]) => {
        setCheckedIds(ids)
    }

    const handleClick = async () => {
        checkedIds.forEach(funcionarioId =>
            mutate(
                { funcionarioId, repId: id },
                {
                    onSuccess: () => {
                        toast.success('Funcionário associado com sucesso')
                    },

                    onError: () => {
                        toast.error('Erro ao associar funcionário')

                        setCheckedIds(prev => [...prev, funcionarioId])
                    },
                },
            ),
        )

        setCheckedIds([])
    }

    const enviarFuncionarios = (funcionarioIds?: number[]) => {
        enviar(
            { repId: id, funcionarioIds },
            {
                onSuccess: ({ enviados, success, message }) => {
                    if (!success) {
                        toast.error(
                            message ??
                                'Equipamento não recebeu os funcionários enviados',
                        )
                        return
                    }

                    toast.success(
                        `${enviados} funcionário(s) enviado(s) ao equipamento`,
                    )
                },
                onError: err => {
                    toast.error(
                        err.message ||
                            'Erro ao enviar os funcionário ao equipamento.',
                    )
                },
                onSettled: () => {
                    if (!funcionarioIds?.length) return

                    setEmVoo(prev =>
                        prev.filter(it => !funcionarioIds.includes(it)),
                    )
                },
            },
        )
    }

    // cliques em linhas diferentes viram um único envio em bloco
    useEffect(() => {
        if (!pendentesDebounced.length) return

        setEmVoo(prev => [...prev, ...pendentesDebounced])
        enviarFuncionarios(pendentesDebounced)
        setPendentes([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendentesDebounced])

    const envio = useMemo<IEnvioContext>(
        () => ({
            estaEnviando: funcionarioId =>
                pendentes.includes(funcionarioId) ||
                emVoo.includes(funcionarioId),
            solicitarEnvio: funcionarioId =>
                setPendentes(prev =>
                    prev.includes(funcionarioId)
                        ? prev
                        : [...prev, funcionarioId],
                ),
        }),
        [pendentes, emVoo],
    )

    return (
        <EnvioContext.Provider value={envio}>
            <Grid container className="items-start">
                <Grid item xs={9}>
                    <SelectFuncionarios
                        variant="form"
                        form
                        checked={checked}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Button
                        fullWidth
                        size="large"
                        onClick={handleClick}
                        disabled={checkedIds.length === 0}
                    >
                        Associar
                    </Button>
                </Grid>

                <Grid item xs={12} className="flex justify-end pb-4">
                    <Tooltip
                        title={
                            <span className="w-xs">
                                Envia todos os funcionários para o equipamento
                                (se compatível)
                            </span>
                        }
                    >
                        <Button
                            color="secondary"
                            variant="outlined"
                            startIcon={<SendIcon />}
                            onClick={() => enviarFuncionarios()}
                            disabled={isEnviando || associados.length === 0}
                        >
                            Enviar todos
                        </Button>
                    </Tooltip>
                </Grid>

                <Grid item xs={12} className="h-100">
                    <ContentGuide.DataGrid
                        loading={isLoading}
                        density="compact"
                        columns={[
                            {
                                field: 'Funcionario',
                                headerName: 'Nome',
                                flex: 1,
                                valueGetter: params =>
                                    params.row.Funcionario.nome,
                            },
                            {
                                ...optionsColumn,
                                field: '__enviar',
                                renderCell: EnviarFuncionarioCell,
                            },
                            {
                                ...optionsColumn,
                                field: '__desassociar',
                                renderCell: DesassociarFuncionarioRep,
                            },
                        ]}
                        rows={associados}
                        ToolbarProps={{ hideToolbar: true }}
                    />
                </Grid>

                <FormField name="id" hidden />
            </Grid>
        </EnvioContext.Provider>
    )
}
